import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProduccionSkuPicker from './ProduccionSkuPicker';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { parseKg, formatKg, formatDate } from '../utils/produccion';
import useWideLayout from '../utils/useWideLayout';
import '../styles/produccion.css';

const DEBOUNCE_MS = 400;
const PARAMS = [
  { key: 'temperature', label: 'Temperatura (°C)' },
  { key: 'thickness', label: 'Espesor (mm)' },
  { key: 'weight_reference', label: 'Peso feta (g)' },
];
// Raw material is pulled in kg, so a SKU stocked in units would be deducted as units.
const UNIT_WARNING = 'Este producto se cuenta por unidades en bodega, no en kg: lo que se retire se '
  + 'descontaría como unidades. Si se usa por unidad (latas, envases, bolsas), agrégalo como insumo al cerrar.';

// Optional process parameter: '' -> undefined (omit), junk -> NaN (invalid).
function parseParam(text) {
  const clean = String(text || '').trim().replace(',', '.');
  if (clean === '') return undefined;
  const n = Number(clean);
  return Number.isFinite(n) ? n : NaN;
}

const paramText = (value) => (value === null || value === undefined ? '' : String(Number(value)).replace('.', ','));

function FefoPreview({ line }) {
  const { preview } = line;
  if (!preview) return <div className="prod-sub">Escribe los kg para ver el stock.</div>;
  if (preview.loading) return <div className="prod-sub">Calculando stock…</div>;
  if (preview.error) return <div className="prod-bad">{preview.error}</div>;
  const { data } = preview;
  return (
    <div className="prod-fefo">
      {data.lots.map((lot, index) => {
        const used = Number(lot.use) > 0;
        const cls = `prod-lot${used ? ' use' : ''}${lot.expired ? ' expired' : ''}`;
        return (
          <div className={cls} key={`${lot.rack}-${lot.shelf}-${lot.batch}-${index}`}>
            <span>
              {`${lot.rack}-${lot.shelf} · ${formatDate(lot.expiration_date)}`}
              {lot.expired && ' · vencido, no se usa'}
            </span>
            <span>{used ? `usa ${formatKg(lot.use)} kg` : `${formatKg(lot.quantity)} kg`}</span>
          </div>
        );
      })}
      {(data.in_production || []).map((row) => (
        <div className="prod-lot expired" key={row.po_number}>
          <span>{`${row.po_number} · ya en producción`}</span>
          <span>{`${formatKg(row.quantity)} kg`}</span>
        </div>
      ))}
      {data.enough
        ? <div className="prod-ok">{`✓ Hay stock (${formatKg(data.available)} kg en bodega)`}</div>
        : <div className="prod-bad">{`Solo hay ${formatKg(data.available)} kg en bodega. Baja la cantidad o quita este material.`}</div>}
    </div>
  );
}

function ProduccionNueva() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { po_number: poNumber } = useParams();
  const editing = Boolean(poNumber);
  const wide = useWideLayout();
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [loadingOrder, setLoadingOrder] = useState(editing);
  const [saving, setSaving] = useState(false);
  const timers = useRef({});
  const requests = useRef({});
  const counter = useRef(0);
  const poRef = useRef(poNumber);
  poRef.current = poNumber;

  const patchLine = (key, patch) => setLines((current) => current.map(
    (l) => (l.key === key ? { ...l, ...patch } : l),
  ));

  const fetchPreview = async (key, skuId, kg) => {
    const request = (requests.current[key] || 0) + 1;
    requests.current[key] = request;
    const extra = poRef.current ? `&po_number=${encodeURIComponent(poRef.current)}` : '';
    const res = await produccionApi.get(`fefo/?sku_id=${skuId}&quantity=${kg}${extra}`, token);
    if (requests.current[key] !== request) return;
    patchLine(key, {
      preview: res.ok ? { data: res.data } : { error: errorMessage(res) },
    });
  };

  const schedulePreview = (key, skuId, text, immediate) => {
    clearTimeout(timers.current[key]);
    requests.current[key] = (requests.current[key] || 0) + 1;
    const kg = parseKg(text);
    if (kg === null) {
      patchLine(key, { preview: null });
      return;
    }
    patchLine(key, { preview: { loading: true } });
    if (immediate) {
      fetchPreview(key, skuId, kg);
    } else {
      timers.current[key] = setTimeout(() => fetchPreview(key, skuId, kg), DEBOUNCE_MS);
    }
  };

  // The unit saved on the SKU wins; without it, the one its Loken orders suggest.
  const fetchUnit = async (key, skuId) => {
    const res = await produccionApi.get(`skus/${skuId}/hints/`, token);
    if (res.ok && res.data) patchLine(key, { unit: res.data.stock_unit || res.data.suggested_unit });
  };

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  useEffect(() => {
    if (!editing) return undefined;
    let cancelled = false;
    (async () => {
      const res = await produccionApi.get(`orders/${encodeURIComponent(poNumber)}/`, token);
      if (cancelled) return;
      setLoadingOrder(false);
      if (!res.ok) {
        setError(errorMessage(res));
        return;
      }
      const order = res.data;
      setProducts(order.products || (order.product ? [order.product] : []));
      setNotes(order.notes || '');
      const loaded = order.lines.map((l) => ({
        key: `l${l.id}`,
        sku: l.sku,
        kg: paramText(l.planned),
        open: false,
        params: {
          temperature: paramText(l.temperature),
          thickness: paramText(l.thickness),
          weight_reference: paramText(l.weight_reference),
        },
        preview: null,
      }));
      counter.current = loaded.length;
      setLines(loaded);
      if (loaded.length) setSelected(loaded[0].key);
      loaded.forEach((l) => {
        schedulePreview(l.key, l.sku.id, l.kg, true);
        fetchUnit(l.key, l.sku.id);
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, poNumber, token]);

  const addLine = (sku) => {
    counter.current += 1;
    const key = `n${counter.current}`;
    setLines((current) => [...current, {
      key, sku, kg: '', open: false, preview: null,
      params: { temperature: '', thickness: '', weight_reference: '' },
    }]);
    setSelected(key);
    fetchUnit(key, sku.id);
  };

  const removeLine = (key) => {
    clearTimeout(timers.current[key]);
    requests.current[key] = (requests.current[key] || 0) + 1;
    setLines((current) => current.filter((l) => l.key !== key));
  };

  const changeKg = (line, text) => {
    patchLine(line.key, { kg: text });
    setSelected(line.key);
    schedulePreview(line.key, line.sku.id, text, false);
  };

  const changeParam = (line, name, text) => patchLine(line.key, {
    params: { ...line.params, [name]: text },
  });

  // Why the submit button is off (empty when it can be pressed).
  let reason = '';
  if (products.length === 0) reason = 'Elige qué vas a producir.';
  else if (lines.length === 0) reason = 'Agrega al menos una materia prima.';
  else if (lines.some((l) => parseKg(l.kg) === null)) reason = 'Revisa los kg de cada materia prima.';
  else if (lines.some((l) => PARAMS.some((p) => Number.isNaN(parseParam(l.params[p.key]))))) {
    reason = 'Revisa los parámetros de laminado.';
  } else if (lines.some((l) => !l.preview || l.preview.loading)) reason = 'Calculando stock…';
  else if (lines.some((l) => l.preview.error || !l.preview.data.enough)) {
    reason = 'Falta stock en alguna materia prima.';
  }

  const submit = async () => {
    setError('');
    setSaving(true);
    const body = {
      notes,
      lines: lines.map((l) => {
        const out = { sku_id: l.sku.id, quantity: parseKg(l.kg) };
        PARAMS.forEach((p) => {
          const value = parseParam(l.params[p.key]);
          if (value !== undefined) out[p.key] = value;
        });
        return out;
      }),
    };
    body.product_ids = products.map((p) => p.id);
    const res = await produccionApi.send(
      editing ? 'PUT' : 'POST',
      editing ? `orders/${encodeURIComponent(poNumber)}/` : 'orders/',
      body,
      token,
    );
    setSaving(false);
    if (!res.ok) {
      setError(errorMessage(res));
      return;
    }
    navigate('/production');
  };

  const selectedLine = lines.find((l) => l.key === selected);
  const excludeIds = lines.map((l) => l.sku.id);

  const renderLine = (line) => {
    const short = line.preview && line.preview.data && !line.preview.data.enough;
    return (
      <div
        key={line.key}
        className={`prod-line${selected === line.key ? ' sel' : ''}${short ? ' short' : ''}`}
        role="presentation"
        onClick={() => setSelected(line.key)}
      >
        <div className="prod-line-head">
          <b>{line.sku.name}</b>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              className="prod-input kg"
              inputMode="decimal"
              aria-label={`Kg de ${line.sku.name}`}
              placeholder="kg"
              value={line.kg}
              onChange={(e) => changeKg(line, e.target.value)}
            />
            <button
              type="button"
              className="prod-rm"
              aria-label={`Quitar ${line.sku.name}`}
              onClick={(e) => { e.stopPropagation(); removeLine(line.key); }}
            >
              ✕
            </button>
          </div>
        </div>
        {line.unit === 'un' && <div className="prod-warn">{UNIT_WARNING}</div>}
        <div className="prod-params">
          <button
            type="button"
            className="prod-btn ghost"
            style={{ width: 'auto', margin: 0, padding: '4px 10px', fontSize: 13 }}
            aria-expanded={line.open}
            onClick={(e) => { e.stopPropagation(); patchLine(line.key, { open: !line.open }); }}
          >
            {`${line.open ? '▾' : '▸'} Parámetros de laminado (opcional)`}
          </button>
          {line.open && (
            <div className="prod-params-grid">
              {PARAMS.map((p) => (
                <label key={p.key} className="prod-lbl">
                  {p.label}
                  <input
                    className="prod-input"
                    inputMode="decimal"
                    value={line.params[p.key]}
                    onChange={(e) => changeParam(line, p.key, e.target.value)}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
        {!wide && <FefoPreview line={line} />}
      </div>
    );
  };

  const form = (
    <div>
      <div className="prod-lbl">¿Qué vas a producir?</div>
      {products.map((p) => (
        <div key={p.id} className="prod-card green" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><b>{p.name}</b> <span className="prod-sub">{p.code}</span></span>
          <button
            type="button"
            className="prod-btn ghost"
            style={{ width: 'auto', margin: 0, padding: '4px 10px', fontSize: 13 }}
            aria-label={`Quitar ${p.name}`}
            onClick={() => setProducts((current) => current.filter((x) => x.id !== p.id))}
          >
            ✕
          </button>
        </div>
      ))}
      {/* Several products can come out of the same raw material. */}
      <ProduccionSkuPicker
        placeholder="Escanea o escribe el producto a elaborar"
        excludeIds={products.map((p) => p.id)}
        onSelect={(sku) => setProducts((current) => [...current, sku])}
        autoFocus={products.length === 0}
      />

      <div className="prod-lbl" style={{ marginTop: 14 }}>Materia prima</div>
      {lines.map(renderLine)}
      {/* The product is scanned first: this field only takes the focus once there is one. */}
      <ProduccionSkuPicker
        placeholder="Escanea o escribe la materia prima"
        excludeIds={excludeIds}
        onSelect={addLine}
        autoFocus={products.length > 0}
      />

      <label className="prod-lbl" style={{ display: 'block', marginTop: 14 }}>
        Notas (opcional)
        <textarea className="prod-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      {error && <div className="prod-bad prod-error">{error}</div>}

      <div className="prod-submit-row">
        <button type="button" className="prod-btn green" disabled={Boolean(reason) || saving} onClick={submit}>
          {editing ? 'Guardar cambios' : 'Crear y enviar a retiro'}
        </button>
        {reason && <span className="prod-sub" style={{ margin: 0 }}>{reason}</span>}
      </div>
    </div>
  );

  return (
    <div className="prod-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <h1 className="prod-title">{editing ? `Editar ${poNumber}` : 'Nueva producción'}</h1>
        <button type="button" className="prod-btn ghost" style={{ width: 'auto', margin: 0, padding: '8px 12px', fontSize: 14 }} onClick={() => navigate('/production')}>
          ← Volver
        </button>
      </div>
      {loadingOrder ? <div className="prod-sub">Cargando…</div> : (
        wide ? (
          <div className="prod-split">
            {form}
            <div className="prod-side">
              <div className="prod-lbl">{selectedLine ? `Stock de ${selectedLine.sku.name}` : 'Stock'}</div>
              {selectedLine ? <FefoPreview line={selectedLine} /> : <div className="prod-sub">Agrega una materia prima para ver su stock.</div>}
            </div>
          </div>
        ) : form
      )}
    </div>
  );
}

export default ProduccionNueva;
